import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import type { CaseLeg, LegStatus } from '@/types/domain'

import { LegPlace } from './LegPlace'
import { routeStops } from './routeStops'

/* The route as ONE line, above the cards that detail it (UX reference, Aug 26).
 *
 * The section used to open straight onto a stack of leg cards, and a stack does
 * not read as a chain: "Guangzhou → Khorgos", "Khorgos → Türkmenabat",
 * "Türkmenabat → Ashgabat" is one journey typed three times, and the reader
 * joins it up themselves every time they open the tab. This says it once.
 *
 * WHAT IT IS NOT: the overview's RouteCard. That one shows the case's declared
 * stops — POL, border crossing, POD, unloading place — each captioned with a
 * state the SERVER decides, and it is the answer to "where does this shipment
 * go". This shows the LEG CHAIN, which is a different list and can differ from
 * it: a case declared Guangzhou → Ashgabat may be run over three legs through
 * two borders. Both are honest; neither re-derives the other.
 *
 * And nothing here invents a stop state. Each SEGMENT is exactly one leg and is
 * tinted by that leg's own served status — the same fact the card below prints
 * in words. The DOTS stay neutral: a dot is a handover point, and saying "the
 * cargo has passed this one" would be the second definition of where the cargo
 * is that `legnow` exists to prevent. Where it is is marked once, on the rail
 * beside the cards. */

/** The tone a segment carries — its leg's status, restated in words on the card
 *  below (DESIGN.md § Colour: colour never carries the state alone). */
const SEGMENT: Record<LegStatus, string> = {
  planned: 'bg-line',
  'in-transit': 'bg-brand',
  'waiting-border': 'bg-attention',
  arrived: 'bg-positive',
  completed: 'bg-positive',
}

export function RouteStrip({ legs }: { legs: CaseLeg[] }) {
  const { t } = useTranslation()
  const format = useFormat()

  const stops = routeStops(legs)
  if (stops.length < 2) return null

  /* The span is the FIRST departure and the LAST arrival, in route order — not
     the earliest and latest dates on the route. The two differ exactly when
     somebody has mis-dated a leg, and the route order is the fact worth
     showing: a chain whose "end" is before its start is visible, where a
     silently sorted range would look correct. */
  const from = legs[0]?.etd
  const to = legs[legs.length - 1]?.eta

  return (
    <div className="border-line bg-surface rounded-md border px-5 py-4">
      <ol className="flex items-baseline gap-3">
        {stops.map((stop, index) => (
          <li
            key={`${stop.name}-${index}`}
            className={cn(
              'text-text-heading min-w-0 flex-1 truncate text-[13px] leading-tight font-medium',
              index === 0
                ? 'text-left'
                : index === stops.length - 1
                  ? 'text-right'
                  : 'text-center',
            )}
          >
            <LegPlace name={stop.name} country={stop.country} />
          </li>
        ))}
      </ol>

      {/* The track. Aria-hidden entire: every fact on it — the places, and the
          status of every leg — is already readable above and in the cards
          below, so a screen reader meets the chain as text rather than as a row
          of unlabelled dots. */}
      <div aria-hidden className="mt-2.5 flex items-center">
        {stops.map((stop, index) => (
          <Fragment key={`${stop.name}-${index}-mark`}>
            {index > 0 ? (
              <span
                className={cn(
                  'h-[3px] flex-1 rounded-full',
                  SEGMENT[legs[index - 1]!.status],
                )}
              />
            ) : null}
            <span className="bg-neutral-300 ring-surface size-2.5 shrink-0 rounded-full ring-2" />
          </Fragment>
        ))}
      </div>

      {from ? (
        <p className="text-text-muted asa-num mt-2.5 text-center text-[12px] leading-tight font-light">
          {to
            ? t('cases.legs.span', {
                from: format.dateShort(from),
                to: format.dateShort(to),
              })
            : t('cases.legs.spanFrom', { date: format.dateShort(from) })}
        </p>
      ) : null}
    </div>
  )
}
